import { call, put, select, spawn } from 'redux-saga/effects';

import uuidv4 from 'uuid/v4';
import { get } from 'object-path';

import { clearErrorSaga, getOwnId } from './helperSagas';

import * as types from '../constants/ActionTypes';
import { ATTACHMENT_UPLOAD_V1, CHAT_SEND_V1 } from '../constants/ApiEndpoints';

import { addMessage, removeMessage, addError, addMessageFail } from '../actions/chatActions';

import * as AttachmentUtils from '../helpers/AttachmentUtils';
import { httpGet, httpPost } from '../helpers/HttpUtils';
import { logError } from '../helpers/BrowserUtils';

/**
 * Create a message and its attachments locally before sending off to backend.
 * If the backend request fails, dispatches a failed event.
 */
export function* addMessageSaga(action) {
  const { chatId, content, contentType, timeSent, files } = action.payload;

  yield call(clearErrorSaga, action.type);

  const senderId = yield getOwnId();
  const localMessageId = uuidv4();

  // Create Local Attachments
  let localAttachmentIds = [];
  if (get(files, 'length', 0) > 0) {
    localAttachmentIds = yield call(createLocalAttachments, files, localMessageId);
  }

  // Create Local Message
  yield call(createLocalMessage, localMessageId, chatId, content, contentType, localAttachmentIds, senderId, timeSent.toISOString());

  // Send Attachments and Message to the backend
  yield spawn(createAttachmentsAndMessage, action.type, files, localMessageId, chatId, content, contentType, timeSent);
}

/**
 * Message failed. Try to send again.
 */
export function* retryMessageFailSaga(action) {
  const message = action.payload.message;
  const { chatId, content, contentType, attachments } = message;

  // Recreate files
  const files = [];

  for (let i = 0; i < attachments.length; i++) {
    const attachment = yield select(state => state.attachments[attachments[i]]);

    const url = attachment.downloadUrl || attachment.displayUrl;
    const blob = yield call(httpGet, url, { responseType: 'blob' });
    const file = new File([blob.data], attachment.fileName, { type: attachment.mediaType });

    files.push(file);
  }

  yield put(removeMessage(message));
  yield put(addMessage(chatId, content, contentType, files));
}

/**
 * Messaged failed. Delete local message.
 */
export function* deleteMessageFailSaga(action) {
  const message = action.payload.message;
  yield put(removeMessage(message));
}

/**
 ****************************************************
 * Helper Generators
 ****************************************************
 */
function* createAttachmentsAndMessage(actionType, files, localMessageId, chatId, content, contentType, timeSent) {
  try {
    // Create Attachments
    let realAttachmentIds = [];
    if (get(files, 'length', 0) > 0) {
      realAttachmentIds = yield call(createAttachments, files);
    }

    // Create Messages
    yield call(createMessage, chatId, content, contentType, timeSent, realAttachmentIds, localMessageId);
  } catch (e) {
    logError('Add Message Failed', e);
    yield put(addMessageFail(localMessageId));
    yield put(addError(actionType, e));
  }
}

function* createLocalAttachments(files, messageId) {
  const localAttachmentIds = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const url = URL.createObjectURL(file);

    const localAttachment = {
      id: uuidv4(),
      messageId,
      fileName: file.name,
      mediaType: file.type,
      byteCount: file.size,
      downloadUrl: url,
    };

    if (AttachmentUtils.isMediaTypeImage(file.type)) {
      localAttachment['displayUrl'] = url;
    }

    yield put({ type: types.ADD_LOCAL_ATTACHMENT, payload: { attachment: localAttachment } });

    localAttachmentIds.push(localAttachment.id);
  }

  return localAttachmentIds;
}

function* createLocalMessage(id, chatId, content, contentType, attachmentIds, senderId, timeSent) {
  const localMessage = { id, chatId, content, contentType, attachments: attachmentIds, senderId, timeSent };

  return yield put({ type: types.ADD_LOCAL_MESSAGE, payload: { chatId, message: localMessage } });
}

function* createAttachments(files) {
  const formData = new FormData();
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    formData.append('attachments', file);
  }

  const uploadedFiles = yield httpPost(ATTACHMENT_UPLOAD_V1, formData);

  return uploadedFiles.data.attachments.map(meta => meta.id);
}

function* createMessage(chatId, content, contentType, timeSent, attachmentIds, externalId) {
  return yield httpPost(CHAT_SEND_V1, { chatId, content, contentType, timeSent, attachmentIds, externalId });
}
